import { set, isNil } from 'lodash';
import { nextTick } from 'vue';
import { naturalSort } from '@/js/utils/sort-utils';
import attributeFilterValueSelectionModes from '@enums/attribute-filter-value-selection-modes';

const { INCLUDE, EXCLUDE } = attributeFilterValueSelectionModes;

export const sanitizeValues = values => (values || []).filter(value => !isNil(value));

/**
 * Applies available values watcher behaviour to context state.
 * Formats primitives as objects with text/value properties for vuex-select display.
 * @param {Object} context - Component context or state object
 * @param {Array} newValues - Incoming available values from watcher
 */
export function applyAvailableValuesWatcher(context, newValues) {
    const sanitized = sanitizeValues(newValues);
    // Format primitives as objects with text/value for select display
    const formatted = sanitized.map(value => {
        if (typeof value === 'object' && value !== null) {
            return value;
        }
        return {
            text: String(value),
            value,
        };
    });
    const currentOptions = context.atributeValuesOptions || [];

    if (
        formatted.length === currentOptions.length &&
        formatted.every((val, index) => {
            const current = currentOptions[index];
            return current && current.value === val.value;
        })
    ) {
        return;
    }

    context.atributeValuesOptions = formatted;
}

/**
 * Filters attribute options to the backend-scoped attribute keys.
 * @param {Object} context - Component context or state object
 * @returns {Array} Filtered attribute options
 */
export function filteredAttribureOptions(context) {
    const {
        availableAttributeKeys,
        rowAttrubuteKey,
        attributeRow,
        attributeScope,
        getAvailableAttributes,
    } = context;

    const sanitized = sanitizeValues(availableAttributeKeys).map(attributeKey =>
        Number(attributeKey)
    );

    return getAvailableAttributes.filter(
        item =>
            (item.attributeKey === rowAttrubuteKey || sanitized.includes(item.attributeKey)) &&
            !attributeScope.find(
                scopedItem =>
                    scopedItem !== attributeRow && scopedItem.attributeKey === item.attributeKey
            )
    );
}

/**
 * Sets attribute values in the attribute scope.
 * @param {Object} context - Component context or state object
 * @param {Array} values - New values to set
 */
export function setAttributeValues(context, values) {
    const { attributeScope, attributeRowIndex } = context;
    set(attributeScope[attributeRowIndex], 'values', values);
    if (context.$emit) {
        context.$emit('products-number-updated');
    }
}

/**
 * Sets a new attribute for the row.
 * @param {Object} context - Component context or state object
 * @param {*} value - The attribute key value to set
 */
export async function setAttribute(context, value) {
    const {
        attributeRow,
        attributeScope,
        attributeRowIndex,
        getAvailableAttributesKeyedByAttributeKey,
        isReadOnly,
    } = context;

    if (value === attributeRow.attributeKey) {
        return;
    }

    context.isLoading = true;
    context.atributeValuesOptions = [];

    const fullAttributeObject = getAvailableAttributesKeyedByAttributeKey[value];
    const normalizedMode = attributeRow.mode === EXCLUDE ? EXCLUDE : INCLUDE;
    set(attributeScope, attributeRowIndex, {
        attributeKey: fullAttributeObject ? fullAttributeObject.attributeKey : null,
        attributeName: fullAttributeObject ? fullAttributeObject.attributeName : null,
        key: attributeRow.key,
        values: [],
        mode: normalizedMode,
        rangeOperator: null,
        minValue: null,
        maxValue: null,
    });

    if (value) {
        if (context.$refs && context.$refs.form && !isReadOnly) {
            await nextTick();
            context.$refs.form.validate();
        }
    } else if (context.$refs && context.$refs.form) {
        await nextTick();
        context.$refs.form.resetValidation();
    }

    if (context.$emit) {
        context.$emit('products-number-updated');
    }

    context.isLoading = false;
}

/**
 * Naturally sorts options alphabetically.
 * @param {Array} options - Options to sort
 * @returns {Array} Sorted options
 */
export function naturalSortOptions(options) {
    return [...options].sort((a, b) => {
        const aComparable = typeof a === 'object' && a !== null ? a.text ?? a.value ?? '' : a;
        const bComparable = typeof b === 'object' && b !== null ? b.text ?? b.value ?? '' : b;

        return naturalSort(aComparable, bComparable);
    });
}
